import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  AndroidFilled,
  AppleFilled,
  AppstoreOutlined,
  ArrowRightOutlined,
  BellOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloudSyncOutlined,
  DesktopOutlined,
  FileImageOutlined,
  GlobalOutlined,
  LaptopOutlined,
  LayoutOutlined,
  MobileOutlined,
  QrcodeOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  SwapOutlined,
  TabletOutlined,
  TeamOutlined,
  ToolOutlined,
  UsergroupAddOutlined,
  WechatFilled
} from '@ant-design/icons';
import { productShowcases, productShowcaseList } from '../data/productShowcases';
import PublicHeader from '../components/layout/PublicHeader';
import '../assets/css/ProductDetailPage.css';

const iconMap = {
  android: AndroidFilled,
  apple: AppleFilled,
  classroom: AppstoreOutlined,
  clock: ClockCircleOutlined,
  desktop: DesktopOutlined,
  family: UsergroupAddOutlined,
  growth: CloudSyncOutlined,
  global: GlobalOutlined,
  laptop: LaptopOutlined,
  layout: LayoutOutlined,
  media: FileImageOutlined,
  mobile: MobileOutlined,
  notification: BellOutlined,
  online: CloudSyncOutlined,
  scan: QrcodeOutlined,
  shield: SafetyCertificateOutlined,
  sound: SoundOutlined,
  switch: SwapOutlined,
  tablet: TabletOutlined,
  team: TeamOutlined,
  tool: ToolOutlined,
  wechat: WechatFilled
};

const ProductIcon = ({ name }) => {
  const Icon = iconMap[name] || AppstoreOutlined;
  return <Icon />;
};

function ProductMockup({ product }) {
  if (product.key === 'web-management') {
    return (
      <div className="detail-device workbench-device">
        <div className="mock-titlebar"><span /><span /><span /><em>网页管理后台</em></div>
        <div className="mock-workbench-shell">
          <aside><strong>学校工作台</strong><span className="active">运行概况</span><span>班级管理</span><span>教师学生</span><span>设备与广播</span></aside>
          <div className="mock-workbench-main">
            <div className="mock-welcome"><span>学校管理概况</span><strong>业务与设备状态一目了然</strong></div>
            <div className="mock-stat-row"><span><b>36</b>在用班级</span><span><b>34</b>在线设备</span><span><b>12</b>今日任务</span></div>
            <div className="mock-task"><i /><span><b>班级与教师数据</b><small>按学校权限统一维护</small></span><em>去管理</em></div>
            <div className="mock-task"><i /><span><b>今日广播发送记录</b><small>查看任务与教室接收状态</small></span><em>去查看</em></div>
          </div>
        </div>
      </div>
    );
  }

  if (product.key === 'teacher-workbench') {
    return (
      <div className="detail-device workbench-device">
        <div className="mock-titlebar"><span /><span /><span /><em>教师工作台</em></div>
        <div className="mock-workbench-shell">
          <aside><strong>今日工作</strong><span className="active">学校任务</span><span>课堂教学</span><span>班级管理</span><span>学生成长</span></aside>
          <div className="mock-workbench-main">
            <div className="mock-welcome"><span>早上好，王老师</span><strong>今天从这 3 件事开始</strong></div>
            <div className="mock-stat-row"><span><b>08</b>待办任务</span><span><b>03</b>今日课程</span><span><b>42</b>班级学生</span></div>
            <div className="mock-task"><i /><span><b>二年级数学组教研通知</b><small>今天 10:30 前确认</small></span><em>待处理</em></div>
            <div className="mock-task"><i /><span><b>向七年级四班发送课前提醒</b><small>教室客户端在线</small></span><em>去发送</em></div>
          </div>
        </div>
      </div>
    );
  }

  if (product.key === 'classroom-desktop') {
    return (
      <div className="detail-device classroom-device">
        <div className="classroom-screen">
          <div className="classroom-status"><span>七年级四班</span><em>设备在线</em></div>
          <div className="classroom-message"><SoundOutlined /><strong>请同学们准备上交数学作业</strong><span>王老师 · 刚刚</span></div>
          <div className="classroom-dock"><span>横幅提醒</span><span>语音播报</span><span>全屏通知</span></div>
        </div>
        <div className="classroom-stand" />
      </div>
    );
  }

  const isWechat = product.key === 'wechat-mini-program';
  return (
    <div className={`detail-device phone-device ${isWechat ? 'wechat-phone' : ''}`}>
      <div className="phone-frame">
        <div className="phone-island" />
        <div className="phone-status"><span>9:41</span><span>● ◒ ▰</span></div>
        <div className="phone-app-title"><ProductIcon name={product.icon} /><strong>{product.shortName}</strong><span>•••</span></div>
        <div className="phone-greeting"><small>上午好，王老师</small><strong>今天想先处理什么？</strong></div>
        <div className="phone-action-grid"><span><SoundOutlined />发广播</span><span><TeamOutlined />班级</span><span><QrcodeOutlined />扫一扫</span><span><BellOutlined />消息</span></div>
        <div className="phone-card"><span>今日待办</span><strong>完成班级作业反馈</strong><small>七年级四班 · 还剩 6 份</small></div>
        {isWechat && <div className="wechat-home-line">微信小程序 · 无需安装</div>}
      </div>
    </div>
  );
}

const ProductDetailPage = () => {
  const { productKey } = useParams();
  const navigate = useNavigate();
  const normalizedProductKey = ['ios', 'android'].includes(productKey) ? 'mobile-app' : productKey;
  const product = productShowcases[normalizedProductKey];

  useEffect(() => {
    if (!product) return undefined;
    const previousTitle = document.title;
    const description = `${product.name}：${product.description}`;
    const metadata = [
      ['meta[name="description"]', description],
      ['meta[property="og:title"]', `${product.name}｜教室小喇叭`],
      ['meta[property="og:description"]', description],
      ['meta[name="twitter:title"]', `${product.name}｜教室小喇叭`],
      ['meta[name="twitter:description"]', description]
    ];
    const previousMetadata = metadata.map(([selector, content]) => {
      const element = document.querySelector(selector);
      const previousContent = element?.getAttribute('content');
      if (element) element.setAttribute('content', content);
      return [element, previousContent];
    });
    const imageMetadata = [
      document.querySelector('meta[property="og:image"]'),
      document.querySelector('meta[name="twitter:image"]')
    ];
    const previousImages = imageMetadata.map((element) => element?.getAttribute('content'));
    imageMetadata.forEach((element) => element?.setAttribute('content', ''));
    document.title = `${product.name}｜教室小喇叭`;
    window.scrollTo(0, 0);
    return () => {
      document.title = previousTitle;
      previousMetadata.forEach(([element, previousContent]) => {
        if (element) element.setAttribute('content', previousContent || '');
      });
      imageMetadata.forEach((element, index) => {
        if (element) element.setAttribute('content', previousImages[index] || '');
      });
    };
  }, [product]);

  if (!product) return <Navigate to="/products" replace />;

  return (
    <div
      className={`product-detail-page product-detail-${product.key}`}
      style={{ '--detail-accent': product.accent, '--detail-accent-dark': product.accentDark, '--detail-accent-soft': product.accentSoft }}
    >
      <PublicHeader />

      <main>
        <section className="detail-hero">
          <div className="detail-hero-copy">
            <div className="detail-eyebrow"><ProductIcon name={product.icon} /><span>{product.eyebrow}</span><em>{product.status}</em></div>
            <h1>{product.headline.split('\n').map((line) => <React.Fragment key={line}>{line}<br /></React.Fragment>)}</h1>
            <p>{product.description}</p>
            <div className="detail-actions">
              <button className="primary" type="button" onClick={() => navigate(product.primaryAction.path)}>{product.primaryAction.label}<ArrowRightOutlined /></button>
              <button type="button" onClick={() => navigate(product.secondaryAction.path)}>{product.secondaryAction.label}</button>
            </div>
            <div className="detail-audiences">
              <span>适用：</span>{product.audiences.map((audience) => <em key={audience}>{audience}</em>)}
            </div>
          </div>
          <div className="detail-hero-visual"><ProductMockup product={product} /><div className="detail-promise"><CheckCircleFilled /><span>{product.promise}</span></div></div>
        </section>

        <section className="detail-metrics" aria-label="产品概览">
          {product.metrics.map((metric) => <div key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}
          <div className="detail-domain"><span>官网统一入口</span><strong>产品中心内访问</strong><small>无需切换域名或离开教室小喇叭官网</small></div>
        </section>

        <section className="detail-feature-section">
          <div className="detail-section-heading"><span>核心能力</span><h2>为真实使用场景而设计</h2><p>每项能力都围绕用户在这个端上的核心任务展开，同时与其他产品端共享统一的数据和权限。</p></div>
          <div className="detail-feature-grid">
            {product.features.map((feature, index) => (
              <article key={feature.title}><div><ProductIcon name={feature.icon} /><span>{String(index + 1).padStart(2, '0')}</span></div><h3>{feature.title}</h3><p>{feature.text}</p></article>
            ))}
          </div>
        </section>

        <section className="detail-journey-section">
          <div className="detail-section-heading light"><span>使用流程</span><h2>从进入产品，到完成一次工作闭环</h2></div>
          <div className="detail-journey">
            {product.journey.map((item, index) => (
              <React.Fragment key={item.step}>
                <article><span>{item.step}</span><h3>{item.title}</h3><p>{item.text}</p></article>
                {index < product.journey.length - 1 && <ArrowRightOutlined className="journey-arrow" />}
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="detail-scenes-section">
          <div className="detail-section-heading"><span>典型场景</span><h2>一天中，自然发生的使用时刻</h2></div>
          <div className="detail-scenes">
            {product.scenes.map((scene, index) => <article key={scene}><span>0{index + 1}</span><p>{scene}</p><CheckCircleFilled /></article>)}
          </div>
        </section>

        <section className="detail-family-section">
          <div><span>五端产品体系</span><h2>独立介绍，协同工作</h2><p>每个产品都有独立定位与介绍页面，但登录后仍连接同一套学校、班级和用户数据。</p></div>
          <div className="detail-family-links">
            {productShowcaseList.filter((item) => item.key !== product.key).map((item) => (
              <button type="button" key={item.key} onClick={() => navigate(`/products/${item.key}`)}><ProductIcon name={item.icon} /><span><strong>{item.name}</strong><small>{item.eyebrow}</small></span><ArrowRightOutlined /></button>
            ))}
          </div>
        </section>

        <section className="detail-final-cta">
          <ProductIcon name={product.icon} />
          <span>{product.eyebrow}</span>
          <h2>开始了解{product.name}</h2>
          <p>{product.promise}</p>
          <div className="detail-actions centered"><button className="primary" type="button" onClick={() => navigate(product.primaryAction.path)}>{product.primaryAction.label}<ArrowRightOutlined /></button><button type="button" onClick={() => navigate('/products')}>返回产品总览</button></div>
        </section>
      </main>

      <footer className="detail-footer"><span>教室小喇叭 © 2026</span><span>{product.name} · 官网产品中心</span></footer>
    </div>
  );
};

export default ProductDetailPage;
